import { ButtonIcon } from "@fpsoftware/mvp";
import {
  type ArrayPath,
  type Control,
  type FieldArray,
  type FieldPath,
  type FieldValues,
  useFieldArray,
} from "react-hook-form";
import { TbPlus, TbTrash } from "react-icons/tb";
import { DEBT_TYPE_LABELS, DEBT_TYPES } from "shared";
import { Button } from "@/components/Button";
import { NumberInput } from "@/components/form/NumberInput";
import { SelectField } from "@/components/form/SelectField";
import { TextField } from "@/components/form/TextField";
import styles from "./DebtListFields.module.css";

interface DebtListFieldsProps<T extends FieldValues> {
  control: Control<T>;
  /** Path to an array of `{ type, name, balance, interest_rate, monthly_payment }`. */
  name: ArrayPath<T>;
}

/**
 * Editable list of other loans (credit cards, HELOC, auto, etc.) the borrower
 * wants to roll into the refinance.
 */
export function DebtListFields<T extends FieldValues>({
  control,
  name,
}: DebtListFieldsProps<T>) {
  const { fields, append, remove } = useFieldArray({ control, name });

  return (
    <div className={styles.list}>
      {fields.map((field, index) => (
        <div className={styles.debt} key={field.id}>
          <div className={styles.row}>
            <SelectField
              label="Type"
              required
              control={control}
              name={`${name}.${index}.type` as FieldPath<T>}
            >
              {DEBT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {DEBT_TYPE_LABELS[type]}
                </option>
              ))}
            </SelectField>
            <TextField
              label="Name"
              required
              control={control}
              name={`${name}.${index}.name` as FieldPath<T>}
            />
          </div>
          <div className={styles.row}>
            <NumberInput
              label="Balance"
              required
              control={control}
              name={`${name}.${index}.balance` as FieldPath<T>}
            />
            <NumberInput
              label="Interest rate (%)"
              required
              dp={4}
              control={control}
              name={`${name}.${index}.interest_rate` as FieldPath<T>}
            />
            <NumberInput
              label="Monthly payment"
              required
              control={control}
              name={`${name}.${index}.monthly_payment` as FieldPath<T>}
            />
            <ButtonIcon
              icon={TbTrash}
              className={styles.debtDelete}
              onClick={() => remove(index)}
            >
              Delete
            </ButtonIcon>
          </div>
        </div>
      ))}
      <div>
        <Button
          icon={TbPlus}
          variant="ghost"
          onClick={() =>
            append({
              type: "credit_card",
              name: "",
              balance: "",
              interest_rate: "",
              monthly_payment: "",
            } as FieldArray<T, ArrayPath<T>>)
          }
        >
          Add loan
        </Button>
      </div>
    </div>
  );
}
